import React, { useState, useEffect, useMemo } from "react";
import { Nav, Badge } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FaUsers,
  FaReceipt,
  FaSignOutAlt,
  FaTachometerAlt,
  FaUser,
  FaBars,
  FaTimes,
  FaUserShield,
  FaClock,
  FaSyncAlt,
} from "react-icons/fa";
import { gastosService } from "../services/api";

export const SidebarToggleButton = ({ onClick }) => (
  <button
    className="sidebar-toggle-btn d-md-none"
    onClick={onClick}
    aria-label="Abrir menú"
    style={{
      position: "fixed",
      top: 16,
      left: 16,
      zIndex: 1100,
      background: "#343a40",
      color: "#fff",
      border: "none",
      borderRadius: "50%",
      width: 44,
      height: 44,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    }}
  >
    <FaBars size={22} />
  </button>
);

const Sidebar = ({ show, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const [pendingCounts, setPendingCounts] = useState({ gastos: 0, jornadas: 0 });

  const fetchCounts = React.useCallback(async () => {
    try {
      // Calcular 'fecha_hasta' como ayer a las 00:00 en formato YYYY-MM-DD
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yyyy = yesterday.getFullYear();
      const mm = String(yesterday.getMonth() + 1).padStart(2, "0");
      const dd = String(yesterday.getDate()).padStart(2, "0");
      const fecha_hasta = `${yyyy}-${mm}-${dd}`;

      const [gastosRes, jornadasRes] = await Promise.all([
        gastosService.getGastosPendingCount({}),
        gastosService.getJornadasPendingCount({ fecha_hasta }),
      ]);
      setPendingCounts({
        gastos: gastosRes.data?.count || 0,
        jornadas: jornadasRes.data?.count || 0,
      });
    } catch (error) {
      // Silenciar errores
    }
  }, []);

  useEffect(() => {
    if (user) fetchCounts();
  }, [user, fetchCounts]);

  // Escuchar eventos globales para refrescar contadores
  useEffect(() => {
    const handler = () => fetchCounts();
    window.addEventListener("refreshPendingCounts", handler);
    return () => window.removeEventListener("refreshPendingCounts", handler);
  }, [fetchCounts]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const menuItems = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: <FaTachometerAlt />,
      show: false, // Oculto para todos
    },
    {
      path: "/administradores",
      label: "Administradores bot",
      icon: <FaUserShield />,
      show: isAdmin,
    },
    {
      path: "/usuarios",
      label: "Administradores plataforma",
      icon: <FaUsers />,
      show: isAdmin,
    },
    {
      path: "/gastos",
      label: "Gastos",
      icon: <FaReceipt />,
      show: true,
      badge: pendingCounts.gastos,
    },
    {
      path: "/jornadas",
      label: "Jornadas",
      icon: <FaClock />,
      show: true,
      badge: pendingCounts.jornadas,
    },
    {
      path: "/empleados",
      label: "Empleados",
      icon: <FaUser />,
      show: isAdmin,
    },
    {
      path: "/sincronizacion-fichajes",
      label: "Sincronización fichajes",
      icon: <FaSyncAlt />,
      show: isAdmin,
    },
  ];

  return (
    <div
      className={`sidebar p-3${show ? " sidebar-mobile-open" : ""}`}
      style={{
        position: "fixed",
        top: 0,
        left: show ? 0 : "-100vw",
        width: "80vw",
        maxWidth: 300,
        height: "100vh",
        background: "#343a40",
        zIndex: 1200,
        transition: "left 0.3s cubic-bezier(.4,0,.2,1)",
        boxShadow: show ? "2px 0 8px rgba(0,0,0,0.15)" : "none",
        display: "block",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <img
          src="https://www.visualmax.net/wp-content/themes/yootheme/cache/75/LOGO-VISUALMAX-BLANCO-754f3b94.webp"
          alt="Visualmax"
          style={{ width: 160, height: "auto", objectFit: "contain" }}
        />
        <button
          className="d-md-none"
          onClick={onClose}
          aria-label="Cerrar menú"
          style={{
            background: "none",
            border: "none",
            color: "#fff",
            fontSize: 28,
          }}
        >
          <FaTimes />
        </button>
      </div>
      <div className="mb-4">
        <div className="text-white" style={{ fontWeight: 600 }}>
          {useMemo(() => {
            if (!user) return "";
            return (
              user.DISPLAYNAME ||
              user.DISPLAY_NAME ||
              user.NOMBRE ||
              user.nombre ||
              user.EMPLEADO ||
              user.empleado ||
              user.NAME ||
              user.name ||
              user.USERNAME ||
              user.username ||
              user.EMAIL ||
              user.email ||
              user.CREW_ID ||
              ""
            );
          }, [user])}
        </div>
      </div>
      <Nav className="flex-column">
        {menuItems.map((item) => {
          let isActive = false;
          if (item.path === "/gastos") {
            isActive = location.pathname.startsWith("/gastos");
          } else if (item.path === "/jornadas") {
            isActive = location.pathname.startsWith("/jornadas");
          } else if (item.path === "/empleados") {
            isActive = location.pathname.startsWith("/empleados");
          } else if (item.path === "/administradores") {
            isActive = location.pathname.startsWith("/administradores");
          } else {
            isActive = location.pathname === item.path;
          }
          return (
            item.show && (
              <Nav.Link
                key={item.path}
                href={item.path}
                className={isActive ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.path);
                  if (onClose) onClose();
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  {item.icon}
                  <span>{item.label}</span>
                  {typeof item.badge === "number" && item.badge > 0 && (
                    <Badge bg="danger" pill>
                      {item.badge}
                    </Badge>
                  )}
                </span>
              </Nav.Link>
            )
          );
        })}
      </Nav>
      <hr className="text-white" />
      <Nav className="flex-column">
        <Nav.Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleLogout();
            if (onClose) onClose();
          }}
          className="text-danger"
        >
          <FaSignOutAlt /> Cerrar Sesión
        </Nav.Link>
      </Nav>
    </div>
  );
};

export default Sidebar;
